import { ApolloQueryResult } from '@apollo/client';
import { Button } from '@shared/components';
import { ShipmentQuery } from '@shared/generated/graphql';
import { ScheduleMethod } from 'clerk_common/enums';
import { ScheduleStop } from 'clerk_common/types';
import { LiaCalendarSolid } from 'react-icons/lia';
import { APIPortalStop } from './APIPortalStop';
import { BlueYonderStop } from './BlueYonderStop';
import { EmailStop } from './EmailStop';
import { UnknownStop } from './UnknownStop';
import { WebPortalStop } from './WebPortalStop';

export type StopProps = {
  stop: ScheduleStop;
  index: number;
  shipmentId: string;
  refetchShipment: () => Promise<ApolloQueryResult<ShipmentQuery>>;
};

export function Stop({ stop, index, shipmentId, refetchShipment }: StopProps) {
  const stopType = determineStopTypeUsingScheduleMethod(stop);

  switch (stopType) {
    case 'email':
      return (
        <EmailStop
          stop={stop}
          index={index}
          shipmentId={shipmentId}
          refetchShipment={refetchShipment}
        />
      );
    case 'apiportal':
      return (
        <APIPortalStop
          stop={stop}
          index={index}
          shipmentId={shipmentId}
          refetchShipment={refetchShipment}
        />
      );
    case 'webportal':
      return (
        <WebPortalStop
          stop={stop}
          index={index}
          shipmentId={shipmentId}
          refetchShipment={refetchShipment}
        />
      );
    case 'blueyonder':
      return (
        <BlueYonderStop
          stop={stop}
          index={index}
          shipmentId={shipmentId}
          refetchShipment={refetchShipment}
        />
      );
    case 'unknown':
      return (
        <UnknownStop
          stop={stop}
          index={index}
          shipmentId={shipmentId}
          refetchShipment={refetchShipment}
        />
      );
    default:
      return null;
  }
}

function determineStopTypeUsingScheduleMethod(
  stop: ScheduleStop
): 'email' | 'apiportal' | 'webportal' | 'blueyonder' | 'unknown' {
  if (!stop.scheduleMethod) return 'unknown';
  switch (stop.scheduleMethod) {
    case ScheduleMethod.EMAIL:
      return 'email';
    case ScheduleMethod.OPENDOCK:
      return 'apiportal';
    case ScheduleMethod.BLUE_YONDER:
      return 'blueyonder';
    default:
      return 'webportal';
  }
}

export function ScheduleButton({
  disabled,
  onPress,
  label = 'Schedule',
}: {
  disabled: boolean;
  onPress: () => void;
  label?: string;
}) {
  return (
    <Button
      size="xs"
      variant="secondary"
      icon={<LiaCalendarSolid size={12} />}
      iconPosition="right"
      className="ml-auto"
      isDisabled={disabled}
      onPress={onPress}
    >
      {label}
    </Button>
  );
}
